// src/components/Mapa.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Carregando from "./Carregando";
import PainelDetalhe, { Campo } from "./PainelDetalhe";
import ListaFiltro from "./ListaFiltro";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
// Em produção o site lê a tabela noticias_mapa direto do Supabase (gerada por
// backend/publicar_mapa.py); sem estas variáveis, usa a API local.
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
// Limite de linhas por resposta do PostgREST no Supabase (max-rows padrão).
const SUPABASE_PAGINA = 1000;

async function buscarTabelaSupabase(tabela, ordem) {
  const linhas = [];
  for (let offset = 0; ; offset += SUPABASE_PAGINA) {
    const resp = await axios.get(`${SUPABASE_URL}/rest/v1/${tabela}`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
      params: { select: "*", order: ordem, limit: SUPABASE_PAGINA, offset },
    });
    linhas.push(...resp.data);
    if (resp.data.length < SUPABASE_PAGINA) return linhas;
  }
}

async function buscarApiLocal(caminho) {
  const resp = await axios.get(`${API_URL}${caminho}`, {
    headers: { "Cache-Control": "no-cache" },
    params: { _: Date.now() },
  });
  return resp.data;
}

// Fonte única das notícias, usada pelo mapa e pela página Sobre.
export async function buscarNoticias() {
  if (SUPABASE_URL && SUPABASE_ANON_KEY) return buscarTabelaSupabase("noticias_mapa", "id");
  return buscarApiLocal("/noticias");
}

// Casos de Planejamento Conflitual (aba Casos).
export async function buscarCasos() {
  if (SUPABASE_URL && SUPABASE_ANON_KEY) return buscarTabelaSupabase("casos_planejamento", "codigo");
  return buscarApiLocal("/casos");
}
const NOTICIAS_POR_PAGINA = 5;

const SUGESTOES_NOTICIA = [
  "O que aconteceu, em poucas palavras?",
  "Quem está envolvido no conflito?",
  "O que estão reivindicando?",
  { texto: "O que aconteceu depois disso?", internet: true },
];
// Fora de localhost o CARTO exige chave (gratuita); sem ela, a URL antiga só
// funciona no servidor de desenvolvimento.
const CARTO_KEY = process.env.REACT_APP_CARTO_KEY;
export const TILE_URL = CARTO_KEY
  ? `https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`
  : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

// Cor de cada categoria: usada só nos gráficos da página Sobre. No mapa os
// pontos têm uma cor só e o filtro diz a categoria.
export const CATEGORIAS_CONFIG = {
  todos: { cor: "#6c757d", nome: "Todos os conflitos", descricao: "Visualizar todos" },
  transporte: { cor: "#2196F3", nome: "Transporte", descricao: "Mobilidade e infraestrutura" },
  moradia: { cor: "#E53935", nome: "Moradia", descricao: "Habitação e ocupações" },
  educação: { cor: "#4CAF50", nome: "Educação", descricao: "Ensino e escolas" },
  segurança: { cor: "#FF9800", nome: "Segurança", descricao: "Violência e proteção" },
  saúde: { cor: "#E91E63", nome: "Saúde", descricao: "Acesso e infraestrutura" },
  saneamento_meio_ambiente: { cor: "#00BCD4", nome: "Meio ambiente", descricao: "Saneamento e ecologia" },
  trabalho: { cor: "#9C27B0", nome: "Trabalho", descricao: "Emprego e direitos" },
  direitos_humanos: { cor: "#F44336", nome: "Direitos humanos", descricao: "Lutas e reivindicações" },
  alimentação: { cor: "#FFC107", nome: "Alimentação", descricao: "Segurança alimentar" },
  outros: { cor: "#607D8B", nome: "Outros", descricao: "Demais categorias" },
};

// A cor dos dados no mapa (pontos e seleção) e a tinta do texto.
export const COR_DADO = "#c4302b";
export const COR_TINTA = "#131416";

// Círculo com o total de notícias do local; cresce devagar com o total.
function criarIconeAgrupado(total) {
  const tamanho = Math.round(24 + Math.min(20, Math.sqrt(total) * 3));
  return new L.DivIcon({
    className: "marcador",
    html: `<span class="marcador-grupo" style="width:${tamanho}px;height:${tamanho}px">${total}</span>`,
    iconSize: [tamanho, tamanho],
    iconAnchor: [tamanho / 2, tamanho / 2],
    popupAnchor: [0, -tamanho / 2],
  });
}

// Em um filtro de categoria, cada notícia volta a ter seu próprio ponto.
function criarIcone() {
  return new L.DivIcon({
    className: "marcador",
    html: '<span class="marcador-ponto"></span>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8],
  });
}

// Aproximação planar de distância em metros, suficiente para deslocamentos
// curtos na região do mapa.
const METROS_POR_GRAU_LATITUDE = 111320;

function distanciaMetros(latA, lngA, latB, lngB) {
  const fatorLongitude = Math.cos((((latA + latB) / 2) * Math.PI) / 180);
  return Math.hypot(
    (latB - latA) * METROS_POR_GRAU_LATITUDE,
    (lngB - lngA) * METROS_POR_GRAU_LATITUDE * fatorLongitude,
  );
}

// Converte uma coordenada da API em número válido; null quando ausente.
// Importante: Number(null) === 0, então a checagem precisa ser explícita para
// não transformar "sem coordenada" em um pin no oceano (0, 0).
function parseCoordenada(valor) {
  if (valor === null || valor === undefined || valor === "") return null;
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
}

function obterCoordenadas(item) {
  const latitude = parseCoordenada(item.latitude);
  const longitude = parseCoordenada(item.longitude);
  return latitude === null || longitude === null ? null : [latitude, longitude];
}

// Metros por pixel na latitude média do mapa, para calibrar a separação
// mínima entre pins em função do nível de zoom atual.
const LATITUDE_MEDIA_MAPA = -25.43;

// Distância (em metros) entre dois pins para serem considerados sobrepostos
// no zoom atual; também é a separação aplicada ao distribuí-los. O valor mira
// aproximadamente a largura de um pin (~44 px).
const SEPARACAO_PIXELS_ALVO = 44;

// Deslocamento máximo (em metros) de um pin em relação à SUA PRÓPRIA posição
// original. Garante que a redistribuição nunca tire a notícia do bairro dela:
// os pins só se afastam o suficiente para deixar de se sobrepor.
const DESLOCAMENTO_MAXIMO_METROS = 300;

function calcularSeparacaoMetros(zoom) {
  const metrosPorPixel = (156543.03392 * Math.cos((LATITUDE_MEDIA_MAPA * Math.PI) / 180)) / (2 ** zoom);
  return Math.max(25, SEPARACAO_PIXELS_ALVO * metrosPorPixel);
}

// Limita o alvo redistribuído a um raio máximo a partir da posição real do
// item, encurtando o vetor de deslocamento quando necessário.
function limitarDeslocamento(origemLat, origemLng, alvoLat, alvoLng, maxMetros) {
  const fatorLongitude = Math.cos((origemLat * Math.PI) / 180);
  const deltaLatMetros = (alvoLat - origemLat) * METROS_POR_GRAU_LATITUDE;
  const deltaLngMetros = (alvoLng - origemLng) * METROS_POR_GRAU_LATITUDE * fatorLongitude;
  const distancia = Math.hypot(deltaLatMetros, deltaLngMetros);
  if (distancia <= maxMetros || distancia === 0) return [alvoLat, alvoLng];

  const escala = maxMetros / distancia;
  return [
    origemLat + (deltaLatMetros * escala) / METROS_POR_GRAU_LATITUDE,
    origemLng + (deltaLngMetros * escala) / (METROS_POR_GRAU_LATITUDE * fatorLongitude),
  ];
}

// Pins que ocupariam praticamente o mesmo ponto são agrupados e
// redistribuídos em anéis compactos ao redor do grupo. O deslocamento de cada
// pin é limitado a DESLOCAMENTO_MAXIMO_METROS da posição original dele, de
// modo que cada notícia permaneça na sua região/bairro — apenas deixando de
// ficar escondida atrás das vizinhas.
function separarPinsSobrepostos(itens, obterChave, separacaoMetros) {
  const ordenados = [...itens].sort((a, b) => (
    String(obterChave(a)).localeCompare(String(obterChave(b)))
  ));

  // Agrupamento guloso por proximidade em relação ao centro corrente.
  const aglomerados = [];
  ordenados.forEach((item) => {
    const coordenadas = obterCoordenadas(item);
    if (!coordenadas) return;
    const [latitude, longitude] = coordenadas;

    const alvo = aglomerados.find(({ centro }) => (
      distanciaMetros(centro[0], centro[1], latitude, longitude) <= separacaoMetros
    ));
    if (alvo) {
      const total = alvo.itens.length;
      alvo.centro = [
        (alvo.centro[0] * total + latitude) / (total + 1),
        (alvo.centro[1] * total + longitude) / (total + 1),
      ];
      alvo.itens.push({ item, latitude, longitude });
      return;
    }
    aglomerados.push({ centro: [latitude, longitude], itens: [{ item, latitude, longitude }] });
  });

  const posicoes = new Map();
  aglomerados.forEach(({ centro, itens: grupo }, indiceAglomerado) => {
    if (grupo.length === 1) {
      posicoes.set(obterChave(grupo[0].item), [grupo[0].latitude, grupo[0].longitude]);
      return;
    }

    // Rotação estável por aglomerado para que os anéis não "girem" entre
    // renderizações.
    let hash = 2166136261;
    const semente = String(obterChave(grupo[0].item)) + indiceAglomerado;
    for (let i = 0; i < semente.length; i += 1) {
      hash = Math.imul(hash ^ semente.charCodeAt(i), 16777619);
    }

    // Anel 0 fica no centro; cada anel seguinte comporta cerca de uma pin a
    // cada raio de separação da circunferência. O passo usa o menor valor
    // entre a separação visual e o deslocamento máximo, para que os anéis
    // caibam dentro da área permitida ao redor do grupo.
    const passoAnel = Math.min(separacaoMetros, DESLOCAMENTO_MAXIMO_METROS);
    const slots = [];
    let anel = 0;
    while (slots.length < grupo.length) {
      if (anel === 0) {
        slots.push({ raio: 0, angulo: 0 });
      } else {
        const capacidade = Math.max(1, Math.floor(2 * Math.PI * anel));
        const passoAngulo = (2 * Math.PI) / capacidade;
        const rotacao = ((hash >>> 0) / 4294967296) * passoAngulo + (anel * passoAngulo) / 2;
        for (let i = 0; i < capacidade && slots.length < grupo.length; i += 1) {
          slots.push({ raio: anel * passoAnel, angulo: rotacao + i * passoAngulo });
        }
      }
      anel += 1;
    }

    grupo.forEach((entrada, indice) => {
      const slot = slots[indice];
      const ajusteLongitude = slot.raio / Math.cos((centro[0] * Math.PI) / 180);
      const alvoLat = centro[0] + (slot.raio * Math.sin(slot.angulo)) / METROS_POR_GRAU_LATITUDE;
      const alvoLng = centro[1] + (ajusteLongitude * Math.cos(slot.angulo)) / METROS_POR_GRAU_LATITUDE;
      posicoes.set(
        obterChave(entrada.item),
        limitarDeslocamento(entrada.latitude, entrada.longitude, alvoLat, alvoLng, DESLOCAMENTO_MAXIMO_METROS),
      );
    });
  });
  return posicoes;
}

// Acompanha o zoom atual do mapa para recalcular a distribuição dos pins.
function ObservadorZoom({ aoMudarZoom }) {
  const mapa = useMapEvents({
    zoomend: () => aoMudarZoom(mapa.getZoom()),
  });
  return null;
}

function PopupLocalizacao({ grupo, abrirNoticia }) {
  const [categoriaAberta, setCategoriaAberta] = useState(null);
  const [quantidadeExibida, setQuantidadeExibida] = useState({});
  const popupBodyRef = useRef(null);
  const categoriasDoLocal = Object.entries(grupo.porCategoria)
    .sort(([, noticiasA], [, noticiasB]) => noticiasB.length - noticiasA.length);

  const alternarCategoria = (categoria) => {
    setCategoriaAberta((atual) => {
      if (atual === categoria) return null;

      setQuantidadeExibida((quantidades) => ({
        ...quantidades,
        [categoria]: quantidades[categoria] || NOTICIAS_POR_PAGINA,
      }));
      return categoria;
    });
  };

  useEffect(() => {
    const popupBody = popupBodyRef.current;
    if (!popupBody) return undefined;

    L.DomEvent.disableScrollPropagation(popupBody);
    L.DomEvent.disableClickPropagation(popupBody);
    return undefined;
  }, []);

  return (
    <div className="popup-content popup-location-content">
      <div className="popup-location-header">
        <span className="popup-location-label">{grupo.municipio}</span>
        <h3 className="popup-title">{grupo.local}</h3>
        <span className="popup-location-total">
          {grupo.noticias.length} {grupo.noticias.length === 1 ? "notícia" : "notícias"}
        </span>
      </div>

      <div className="popup-location-body" ref={popupBodyRef}>
        <p className="popup-location-instruction">Escolha um tema para ver as notícias.</p>
        <div className="topic-list">
          {categoriasDoLocal.map(([categoria, noticiasCategoria]) => {
            const config = CATEGORIAS_CONFIG[categoria] || CATEGORIAS_CONFIG.outros;
            const aberta = categoriaAberta === categoria;
            const quantidade = quantidadeExibida[categoria] || NOTICIAS_POR_PAGINA;
            const noticiasVisiveis = noticiasCategoria.slice(0, quantidade);
            const haMaisNoticias = quantidade < noticiasCategoria.length;
            return (
              <div className="topic-group" key={categoria}>
                <button
                  type="button"
                  className={`topic-button ${aberta ? "active" : ""}`}
                  onClick={() => alternarCategoria(categoria)}
                  aria-expanded={aberta}
                >
                  <span>{config.nome}</span>
                  <span className="topic-count num">{noticiasCategoria.length}</span>
                </button>
                {aberta && (
                  <div className="topic-news-panel">
                    <ul className="topic-news-list">
                      {noticiasVisiveis.map((noticia) => (
                        <li key={noticia.id}>
                          <button type="button" onClick={() => abrirNoticia(noticia)}>
                            {noticia.titulo || "Notícia sem título"}
                            {noticia.resumo && <span className="topic-news-resumo">{noticia.resumo}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                    {haMaisNoticias && (
                      <button
                        type="button"
                        className="show-more-news"
                        onClick={() => setQuantidadeExibida((quantidades) => ({
                          ...quantidades,
                          [categoria]: (quantidades[categoria] || NOTICIAS_POR_PAGINA) + NOTICIAS_POR_PAGINA,
                        }))}
                      >
                        Mostrar mais
                      </button>
                    )}
                    {!haMaisNoticias && (
                      <button
                        type="button"
                        className="collapse-news"
                        onClick={() => {
                          setQuantidadeExibida((quantidades) => ({
                            ...quantidades,
                            [categoria]: NOTICIAS_POR_PAGINA,
                          }));
                          setCategoriaAberta(null);
                        }}
                      >
                        Colapsar notícias
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function localDaNoticia(noticia) {
  // O classificador marca "Curitiba (localização não definida)" quando só
  // sabe a cidade; para quem lê, basta a cidade.
  const local = noticia.local || noticia.bairro || noticia.municipio;
  return local ? local.replace(/\s*\(localiza[çc][ãa]o não definida\)/i, "") : "Local não informado";
}

function formatarDataNoticia(iso) {
  if (!iso) return null;
  const [ano, mes, dia] = String(iso).slice(0, 10).split("-");
  return dia ? `${dia}/${mes}/${ano}` : iso;
}

// As manchetes da planilha não têm link próprio (url "planilha://..."); o da
// matéria original, quando o agente a encontrou, vem em detalhes.fonte_url.
function linkDaNoticia(noticia) {
  const link = /^https?:/.test(noticia.url || "") ? noticia.url : noticia.detalhes?.fonte_url;
  return /^https?:/.test(link || "") ? link : null;
}

function FichaNoticia({ noticia }) {
  const d = noticia.detalhes || {};
  return (
    <>
      <dl className="ficha-campos">
        <Campo rotulo="Local">{localDaNoticia(noticia)}</Campo>
        <Campo rotulo="Publicada em">{formatarDataNoticia(noticia.data_publicacao)}</Campo>
      </dl>

      {noticia.resumo ? (
        <section className="ficha-bloco">
          <h3>O que aconteceu <span className="ficha-selo-ia">resumo por IA</span></h3>
          <p>{noticia.resumo}</p>
        </section>
      ) : (
        <p className="ficha-vazia">
          Ainda não há resumo desta notícia. Pergunte ao assistente: ele lê a
          matéria original para responder.
        </p>
      )}

      <dl className="ficha-campos">
        <Campo rotulo="Tipo de ação">{d.tipo_acao}</Campo>
        <Campo rotulo="Participantes">{d.participantes}</Campo>
        <Campo rotulo="Quem se mobiliza" largo>{d.atores}</Campo>
        <Campo rotulo="A quem se dirige" largo>{d.alvo}</Campo>
        <Campo rotulo="Reivindicação" largo>{d.reivindicacao}</Campo>
        <Campo rotulo="Onde exatamente">{d.local}</Campo>
        <Campo rotulo="Quando">{formatarDataNoticia(d.data_evento)}</Campo>
        <Campo rotulo="Desfecho" largo>{d.desfecho}</Campo>
      </dl>

      {linkDaNoticia(noticia) && (
        <a href={linkDaNoticia(noticia)} target="_blank" rel="noreferrer" className="popup-link">
          Ler notícia completa →
        </a>
      )}
    </>
  );
}

function PopupNoticia({ noticia, abrirNoticia }) {
  const config = CATEGORIAS_CONFIG[noticia.categoria] || CATEGORIAS_CONFIG.outros;
  const local = localDaNoticia(noticia);

  return (
    <div className="popup-content">
      <div className="popup-header">
        <span className="popup-category">{config.nome}</span>
        <h3 className="popup-title">{noticia.titulo || "Notícia sem título"}</h3>
      </div>
      <div className="popup-body">
        <p className="popup-info">
          {[local, formatarDataNoticia(noticia.data_publicacao)].filter(Boolean).join(" · ")}
        </p>
        {noticia.resumo && <p className="popup-resumo">{noticia.resumo}</p>}
        <button type="button" className="popup-link" onClick={() => abrirNoticia(noticia)}>
          Ver ficha e perguntar à IA
        </button>
      </div>
    </div>
  );
}

const LARGURA_PAINEL = 440;

// Leva a notícia aberta para a parte do mapa que o painel não cobre.
function EnquadrarNoticia({ noticia }) {
  const mapa = useMap();
  useEffect(() => {
    const coordenadas = noticia && obterCoordenadas(noticia);
    if (!coordenadas) return;
    const ponto = L.latLng(coordenadas[0], coordenadas[1]);
    mapa.flyToBounds(L.latLngBounds(ponto, ponto), {
      paddingBottomRight: [window.innerWidth > 768 ? LARGURA_PAINEL : 0, 0],
      maxZoom: Math.max(mapa.getZoom(), 13),
      duration: 0.8,
    });
  }, [noticia, mapa]);
  return null;
}

// Fecha o popup aberto quando a ficha da notícia é aberta ao lado.
function FecharPopupAoAbrir({ aberta }) {
  const mapa = useMap();
  useEffect(() => {
    if (aberta) mapa.closePopup();
  }, [aberta, mapa]);
  return null;
}

function Mapa({ categoriaInicial = "todos" }) {
  const [todasNoticias, setTodasNoticias] = useState([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState(categoriaInicial);
  const [loading, setLoading] = useState(false);
  const [zoomAtual, setZoomAtual] = useState(10);
  // No celular a barra lateral vira uma gaveta aberta pelo botão "Filtros".
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [noticiaAberta, setNoticiaAberta] = useState(null);

  useEffect(() => {
    carregarNoticias();
  }, []);

  useEffect(() => {
    if (!filtrosAbertos) return undefined;
    const aoTeclar = (evento) => {
      if (evento.key === "Escape") setFiltrosAbertos(false);
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [filtrosAbertos]);

  // Total, filtros, pins e tópicos usam esta mesma lista. Evita que um estado
  // de filtro anterior permaneça no mapa enquanto a barra lateral já mostra
  // dados recém-carregados.
  const noticiasExibidas = useMemo(() => (
    categoriaFiltro === "todos"
      ? todasNoticias
      : todasNoticias.filter((noticia) => noticia.categoria === categoriaFiltro)
  ), [categoriaFiltro, todasNoticias]);

  async function carregarNoticias() {
    try {
      setLoading(true);
      const dados = await buscarNoticias();
      setTodasNoticias(Array.isArray(dados) ? dados : []);
    } catch (err) {
      console.error("Erro ao carregar notícias:", err);
    } finally {
      setLoading(false);
    }
  }

  // Temas com notícia, do maior para o menor.
  const opcoesCategoria = useMemo(() => {
    const contagem = {};
    todasNoticias.forEach((n) => {
      if (CATEGORIAS_CONFIG[n.categoria] && n.categoria !== "todos") {
        contagem[n.categoria] = (contagem[n.categoria] || 0) + 1;
      }
    });
    return Object.entries(contagem)
      .sort((a, b) => b[1] - a[1])
      .map(([id, total]) => ({ id, nome: CATEGORIAS_CONFIG[id].nome, total }));
  }, [todasNoticias]);

  const periodo = useMemo(() => {
    const anos = todasNoticias
      .map((n) => Number(String(n.data_publicacao || "").slice(0, 4)))
      .filter((ano) => ano > 1900);
    if (!anos.length) return null;
    const [primeiro, ultimo] = [Math.min(...anos), Math.max(...anos)];
    return primeiro === ultimo ? String(primeiro) : `${primeiro} a ${ultimo}`;
  }, [todasNoticias]);

  const recentes = useMemo(() => (
    noticiasExibidas
      .filter((n) => n.data_publicacao)
      .sort((a, b) => String(b.data_publicacao).localeCompare(String(a.data_publicacao)))
      .slice(0, 8)
  ), [noticiasExibidas]);
  const gruposPorLocal = useMemo(() => {
    if (categoriaFiltro !== "todos") return [];

    const grupos = new Map();
    noticiasExibidas.forEach((noticia) => {
      const local = noticia.local || noticia.bairro || noticia.municipio || "Localização não definida";
      const municipio = noticia.municipio || "Não definido";
      const chave = `${municipio}::${local}`;
      const grupo = grupos.get(chave) || {
        chave,
        local,
        municipio,
        noticias: [],
        porCategoria: {},
        latitudeTotal: 0,
        longitudeTotal: 0,
        coordenadasValidas: 0,
      };

      grupo.noticias.push(noticia);
      grupo.porCategoria[noticia.categoria] = [
        ...(grupo.porCategoria[noticia.categoria] || []),
        noticia,
      ];
      // A média do grupo usa apenas coordenadas válidas; valores nulos não
      // podem puxar o centro do bairro para outro lugar.
      const coordenadas = obterCoordenadas(noticia);
      if (coordenadas) {
        grupo.latitudeTotal += coordenadas[0];
        grupo.longitudeTotal += coordenadas[1];
        grupo.coordenadasValidas += 1;
      }
      grupos.set(chave, grupo);
    });

    return Array.from(grupos.values())
      .filter((grupo) => grupo.coordenadasValidas > 0)
      .map((grupo) => ({
        ...grupo,
        latitude: grupo.latitudeTotal / grupo.coordenadasValidas,
        longitude: grupo.longitudeTotal / grupo.coordenadasValidas,
      }));
  }, [categoriaFiltro, noticiasExibidas]);

  const quantidadeMarcadores = categoriaFiltro === "todos"
    ? gruposPorLocal.length
    : noticiasExibidas.length;

  // Separação vigente em metros, calibrada pelo zoom corrente do mapa.
  const separacaoAtual = calcularSeparacaoMetros(zoomAtual);
  const posicoesGrupos = useMemo(
    () => separarPinsSobrepostos(gruposPorLocal, (grupo) => grupo.chave, separacaoAtual),
    [gruposPorLocal, separacaoAtual],
  );
  const posicoesNoticias = useMemo(
    () => separarPinsSobrepostos(noticiasExibidas, (noticia) => noticia.id, separacaoAtual),
    [noticiasExibidas, separacaoAtual],
  );

  return (
    <>
      {filtrosAbertos && (
        <div className="sidebar-fundo" onClick={() => setFiltrosAbertos(false)} aria-hidden="true" />
      )}
      <aside id="filtros-mapa" className={`sidebar ${filtrosAbertos ? "open" : ""}`}>
        <div className="lateral-topo">
          <h1 className="lateral-titulo">Radar de conflitos</h1>
          <p className="lateral-linha">
            {loading
              ? "Carregando as notícias…"
              : `${todasNoticias.length} notícias de conflito urbano em Curitiba e região${periodo ? `, de ${periodo}` : ""}.`}
          </p>
        </div>

        <ListaFiltro
          titulo="Tema do conflito"
          rotuloTodas="Todos os temas"
          total={todasNoticias.length}
          opcoes={opcoesCategoria}
          selecionada={categoriaFiltro}
          aoEscolher={(cat) => {
            setCategoriaFiltro(cat);
            setFiltrosAbertos(false);
          }}
        />

        <section className="resultados" aria-label="Notícias mais recentes">
          <h2 className="lateral-secao">
            Mais recentes
            <span className="lateral-secao-nota">
              {categoriaFiltro === "todos" ? "todos os temas" : CATEGORIAS_CONFIG[categoriaFiltro]?.nome}
            </span>
          </h2>
          {!loading && recentes.length === 0 && (
            <p className="lateral-aviso">Nenhuma notícia neste tema ainda.</p>
          )}
          <ul className="registro-lista">
            {recentes.map((noticia) => (
              <li key={noticia.id}>
                <button
                  type="button"
                  className={`registro-item ${noticiaAberta?.id === noticia.id ? "ativo" : ""}`}
                  onClick={() => {
                    setNoticiaAberta(noticia);
                    setFiltrosAbertos(false);
                  }}
                >
                  <span className="registro-titulo">{noticia.titulo || "Notícia sem título"}</span>
                  <span className="registro-meta">
                    {[formatarDataNoticia(noticia.data_publicacao), localDaNoticia(noticia),
                      categoriaFiltro === "todos" ? (CATEGORIAS_CONFIG[noticia.categoria] || CATEGORIAS_CONFIG.outros).nome : null]
                      .filter(Boolean).join(" · ")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      </aside>

      {/* Área do Mapa */}
      <div className="map-area">
        <button
          type="button"
          className="botao-filtros"
          onClick={() => setFiltrosAbertos(true)}
          aria-controls="filtros-mapa"
          aria-expanded={filtrosAbertos}
        >
          Temas e notícias · {CATEGORIAS_CONFIG[categoriaFiltro]?.nome || "Todos os conflitos"}
        </button>
        {loading && (
          <div className="loading-overlay">
            <Carregando texto="Carregando os conflitos…" />
          </div>
        )}
        
        <MapContainer
          center={[-25.43, -49.27]}
          zoom={10}
          className="map-container"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url={TILE_URL}
          />

          <ObservadorZoom aoMudarZoom={setZoomAtual} />
          <FecharPopupAoAbrir aberta={noticiaAberta} />
          <EnquadrarNoticia noticia={noticiaAberta} />

          {categoriaFiltro === "todos" && gruposPorLocal.map((grupo) => {
            return (
              <Marker
                key={`todos:${grupo.chave}:${grupo.noticias.length}`}
                position={posicoesGrupos.get(grupo.chave) || [grupo.latitude, grupo.longitude]}
                icon={criarIconeAgrupado(grupo.noticias.length)}
              >
                <Popup closeOnClick={false}>
                  <PopupLocalizacao grupo={grupo} abrirNoticia={setNoticiaAberta} />
                </Popup>
              </Marker>
            );
          })}
          {categoriaFiltro !== "todos" && noticiasExibidas
            .filter((noticia) => obterCoordenadas(noticia))
            .map((noticia) => (
            <Marker
              key={`${categoriaFiltro}:${noticia.id}`}
              position={posicoesNoticias.get(noticia.id) || [noticia.latitude, noticia.longitude]}
              icon={criarIcone()}
            >
              <Popup>
                <PopupNoticia noticia={noticia} abrirNoticia={setNoticiaAberta} />
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {!loading && quantidadeMarcadores > 0 && (
          <div className="mapa-legenda" aria-label="Legenda do mapa">
            {categoriaFiltro === "todos" ? (
              <span><i className="legenda-grupo" aria-hidden="true">3</i> Notícias no mesmo local <b className="num">{quantidadeMarcadores} locais</b></span>
            ) : (
              <span><i className="legenda-ponto" aria-hidden="true" /> Uma notícia <b className="num">{quantidadeMarcadores}</b></span>
            )}
          </div>
        )}

        {noticiaAberta && (
          <PainelDetalhe
            rotulo={`Notícia · ${(CATEGORIAS_CONFIG[noticiaAberta.categoria] || CATEGORIAS_CONFIG.outros).nome}`}
            titulo={noticiaAberta.titulo || "Notícia sem título"}
            tipo="noticia"
            id={String(noticiaAberta.id)}
            sugestoes={SUGESTOES_NOTICIA}
            aoFechar={() => setNoticiaAberta(null)}
          >
            <FichaNoticia noticia={noticiaAberta} />
          </PainelDetalhe>
        )}
      </div>
    </>
  );
}

export default Mapa;
